import React, {
  useEffect,
  useRef,
  ReactNode,
  Children,
  isValidElement,
  cloneElement,
} from "react";

interface ScrollObserverProps {
  onStepChange: (index: number) => void;
  children: ReactNode;
  rootMargin?: string;
  ignoreIndex?: number;
}

const ScrollObserver: React.FC<ScrollObserverProps> = ({
  onStepChange,
  children,
  rootMargin = "-10% 0px -10% 0px",
  ignoreIndex,
}) => {
  const sectionsRef = useRef<(HTMLElement | null)[]>([]);

  useEffect(() => {
    const sections = sectionsRef.current.filter(
      (section): section is HTMLElement => section !== null
    );
    if (sections.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = sectionsRef.current.indexOf(entry.target as HTMLElement);
            if (index !== -1 && index !== ignoreIndex) {
              onStepChange(index);
            }
          }
        });
      },
      {
        rootMargin,
        threshold: 0.1, // A simpler, more stable threshold
      }
    );

    sections.forEach((section) => observer.observe(section));

    return () => observer.disconnect();
    // 👇 This corrected dependency array makes the observer stable.
  }, [rootMargin, onStepChange, ignoreIndex, children]);

  const setSectionRef = (index: number) => (el: HTMLElement | null) => {
    sectionsRef.current[index] = el;
  };

  return (
    <>
      {Children.map(children, (child, index) => {
        if (isValidElement(child)) {
          return cloneElement(child, { ref: setSectionRef(index) } as any);
        }
        return child;
      })}
    </>
  );
};

export default ScrollObserver;